import React, { useState, useRef } from "react";
import { MoveHorizontal, MapPin, ShieldCheck, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const PROJECT = {
  location: "Vaishali Nagar, Jaipur",
  problem: "Damp, peeling exterior walls & roof seepage after monsoon",
  solution: "Exterior elastomeric coating + roof & terrace waterproofing",
  materials: "Elastomeric Coating, PU Membrane, Crystalline",
  warranty: "12 Years",
  time: "5 Days",
  feedback: "\"The house looks brand new and there's zero dampness now. Outstanding work.\" — Rahul S.",
};

export default function BeforeAfter() {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);
  const dragging = useRef(false);

  const move = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.max(0, Math.min(100, p)));
  };

  return (
    <section className="bg-white py-24 md:py-32" data-testid="before-after">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <div className="text-center mb-12">
            <span className="text-royal text-xs font-semibold tracking-[0.3em] uppercase">Real Transformation</span>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold text-navy-deep mt-3">Before & After</h2>
            <p className="text-slate-600 mt-3">Drag the slider to see the difference our engineering makes.</p>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-3 gap-8 items-center">
          <div className="lg:col-span-2">
            <div
              ref={ref}
              className="relative w-full aspect-[16/10] rounded-lg overflow-hidden select-none border border-slate-200 cursor-ew-resize"
              data-testid="ba-slider"
              onMouseDown={(e) => { dragging.current = true; move(e.clientX); }}
              onMouseMove={(e) => dragging.current && move(e.clientX)}
              onMouseUp={() => (dragging.current = false)}
              onMouseLeave={() => (dragging.current = false)}
              onTouchStart={(e) => move(e.touches[0].clientX)}
              onTouchMove={(e) => move(e.touches[0].clientX)}
            >
              <img src="/before-after-after.png" alt="After waterproofing — clean, protected home" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute top-3 right-3 glass-dark text-white text-xs px-3 py-1 rounded-full">AFTER</div>
              <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
                <img src="/before-after-before.png" alt="Before — damp, water-damaged home" className="absolute inset-0 h-full object-cover" style={{ width: ref.current ? ref.current.getBoundingClientRect().width : "100%", maxWidth: "none" }} />
                <div className="absolute inset-0 bg-black/10" />
                <div className="absolute top-3 left-3 bg-amber-500 text-white text-xs px-3 py-1 rounded-full">BEFORE</div>
              </div>
              <div className="absolute top-0 bottom-0 w-0.5 bg-white" style={{ left: `${pos}%` }}>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-white shadow-lg flex items-center justify-center">
                  <MoveHorizontal className="h-5 w-5 text-royal" />
                </div>
              </div>
            </div>
          </div>

          <Reveal delay={0.15}>
            <div className="bg-soft-grey rounded-lg p-7 border border-slate-200">
              <div className="flex items-center gap-2 text-royal mb-4"><MapPin className="h-4 w-4" /><span className="text-sm font-semibold">{PROJECT.location}</span></div>
              <Item label="Problem" value={PROJECT.problem} />
              <Item label="Solution" value={PROJECT.solution} />
              <Item label="Materials" value={PROJECT.materials} />
              <div className="grid grid-cols-2 gap-4 my-4">
                <div className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-royal" /><div><div className="text-[10px] uppercase text-slate-500">Warranty</div><div className="font-semibold text-navy-deep">{PROJECT.warranty}</div></div></div>
                <div className="flex items-center gap-2"><Clock className="h-5 w-5 text-royal" /><div><div className="text-[10px] uppercase text-slate-500">Completed In</div><div className="font-semibold text-navy-deep">{PROJECT.time}</div></div></div>
              </div>
              <p className="text-slate-600 text-sm italic border-l-2 border-royal pl-3">{PROJECT.feedback}</p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

const Item = ({ label, value }) => (
  <div className="mb-3">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-navy-deep text-sm">{value}</div>
  </div>
);
